import React from 'react';
import { Row, Col, List, Avatar, Collapse } from 'antd';
import InvoiceLabels from '../../../config/InvoiceLabels.json';
import useGlobal from '../../../store/Store';
import _ from 'lodash';
import { ruleTranslations } from '../../../config/Translations';
import { Label } from '../../../types/label';
import {
  isReliableAndNotOnDocument,
  isReliableAndExitsOrManuallyApproved,
  getLabelStatus,
} from '../../../lib/util/status';
import { MultiflowValidationResult } from '../../../types/flow';
import { Icon } from '@ant-design/compatible';
import { SectionTitle } from '../../../components/text/Titles';
import { Section } from '../../../components/Section';

const { Panel } = Collapse;

type Props = {
  hoveredLabel: null | Label;
  onLabelValueMouseEnter: any;
  onLabelValueMouseLeave: any;
  isMultiflow: () => any;
  multiValidationResult: null | MultiflowValidationResult;
  inTable: boolean;
};

export const ResultFields = ({
  hoveredLabel,
  onLabelValueMouseEnter,
  onLabelValueMouseLeave,
  isMultiflow,
  multiValidationResult,
  inTable,
}: Props) => {
  const [globalState] = useGlobal();

  const renderResult = (labelResult: Label) => {
    const notOnDocument = isReliableAndNotOnDocument(labelResult);
    const labelDef = InvoiceLabels.find(it => it.name === labelResult.name);
    return (
      <List
        itemLayout="horizontal"
        dataSource={[labelResult]}
        renderItem={label => (
          <List.Item
            onMouseEnter={() => !notOnDocument && onLabelValueMouseEnter(label)}
            onMouseLeave={() => !notOnDocument && onLabelValueMouseLeave()}
            onClick={() => {}}
            style={{
              cursor: 'pointer',
              padding: '4px 0',
            }}>
            <List.Item.Meta
              avatar={
                <Avatar
                  className={
                    !isReliableAndNotOnDocument(label) && hoveredLabel && label.id === hoveredLabel.id
                      ? 'connectLineB'
                      : ''
                  }
                  shape="square"
                  icon={
                    <Icon
                      type={getLabelStatus(label).icon}
                      style={{
                        color: getLabelStatus(label).color,
                        zIndex: 5000,
                      }}
                    />
                  }
                  style={{
                    backgroundColor: getLabelStatus(label).backgroundColor,
                    cursor: 'pointer',
                  }}
                />
              }
              title={<span>{labelDef ? labelDef.translation : label.name}</span>}
              description={renderValue(label)}
            />
          </List.Item>
        )}
      />
    );
  };

  const renderValue = (label: Label) => {
    const notOnDocument = isReliableAndNotOnDocument(label);
    return (
      <Row>
        <Col span={13}>{!notOnDocument && <span>{label.sourceValue || label.word}</span>}</Col>
      </Row>
    );
  };

  return (
    <Col>
      {!inTable && (
        <Section>
          <SectionTitle>{'Results'}</SectionTitle>
        </Section>
      )}
      <Collapse style={{ backgroundColor: 'white' }} expandIconPosition={'right'} bordered={false} onChange={() => {}}>
        {_.sortBy(globalState.labels, it => {
          const invoiceLabel = InvoiceLabels.find(labelDef => labelDef.name === it.name);
          return invoiceLabel ? invoiceLabel.translation : it.name;
        })
          .filter(it => !it.isTable)
          .map(it => {
            const data = [
              {
                left: 'Location',
                valid: isReliableAndExitsOrManuallyApproved(it),
                right: isReliableAndExitsOrManuallyApproved(it)
                  ? 'Found'
                  : isReliableAndNotOnDocument(it)
                  ? 'Not on invoice'
                  : 'Not reliable',
                color: isReliableAndExitsOrManuallyApproved(it)
                  ? '#52c41a'
                  : isReliableAndNotOnDocument(it)
                  ? '#8c8c8c'
                  : '#fa541c',
              },
              ...(isMultiflow() && !(!it.exists && it.reliable)
                ? [
                    {
                      left: 'Original value',
                      valid: true,
                      right: `"${it.originalWord || it.word}"`,
                      color: '#8c8c8c',
                    },
                    it.prob
                      ? {
                          left: 'Confidence',
                          valid: it.reliable,
                          right: `${Math.round(it.prob * 100).toFixed(0)}%`,
                          color: it.reliable ? '#52c41a' : '#fa541c',
                        }
                      : {},
                  ]
                : []),
              it.type
                ? {
                    left: ruleTranslations[`is${it.type.charAt(0).toUpperCase() + it.type.slice(1)}`],
                    right: it.valid ? 'OK' : 'Not valid',
                    valid: it.valid,
                    color: it.valid ? '#52c41a' : '#fa541c',
                  }
                : null,
            ];

            const rules = it.rules
              ? it.rules.map((rule: any) => ({
                  left: ruleTranslations[rule.name],
                  right: rule.result && rule.result === 'valid' ? 'OK' : 'Not valid',
                  valid: rule.result && rule.result === 'valid',
                  color: rule.result === 'valid' ? '#52c41a' : '#fa541c',
                }))
              : multiValidationResult && multiValidationResult.validation.fields[it.name]
              ? Object.entries(multiValidationResult.validation.fields[it.name].rules).map(([key, value]: any) => ({
                  left: value.description,
                  right: value.success ? 'OK' : 'Not valid',
                  valid: value.success,
                  color: value.success ? '#52c41a' : '#fa541c',
                }))
              : [];

            const dataWithRules = [...data.filter(Boolean), ...rules];
            return (
              <Panel
                style={{
                  backgroundColor: hoveredLabel && it.id === hoveredLabel.id ? '#e6f7ff' : 'transparent',
                }}
                header={renderResult(it)}
                key={it.id}>
                <div
                  style={{
                    padding: '0px 24px',
                    backgroundColor: hoveredLabel && it.id === hoveredLabel.id ? '#e6f7ff' : 'transparent',
                  }}>
                  Process
                  <List
                    size={'small'}
                    bordered={false}
                    split={false}
                    itemLayout="horizontal"
                    dataSource={dataWithRules}
                    renderItem={dataRow =>
                      dataRow && (
                        <List.Item>
                          <List.Item.Meta
                            title={dataRow.left}
                            avatar={
                              <Avatar
                                style={{
                                  backgroundColor: 'transparent',
                                }}
                                size={'small'}
                                icon={
                                  <Icon
                                    type={dataRow.valid ? 'check' : 'exclamation-circle'}
                                    style={{
                                      color: dataRow.valid ? '#52c41a' : '#fa541c',
                                    }}
                                  />
                                }
                              />
                            }
                            style={{ color: dataRow.color }}
                          />
                          <div style={{ color: dataRow.color }}>{dataRow.right}</div>
                        </List.Item>
                      )
                    }
                  />
                </div>
              </Panel>
            );
          })}
      </Collapse>
    </Col>
  );
};
